import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Menu, Search, X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { LOCALES, LOCALE_NAMES, type Taxonomy } from '@shop/shared';
import { api, assetUrl } from '../../lib/api';
import { Link, NavLink, useAlternateHref, useLocale, useT } from '../../lib/i18n';
import { usePages } from '../../lib/pages';
import { useSettings } from '../../lib/settings';
import { Img } from '../ui';
import { SearchDialog } from '../SearchDialog';

export function useTaxonomy() {
  const locale = useLocale();
  const categories = useQuery({ queryKey: ['categories', locale], queryFn: () => api<Taxonomy[]>('/api/store/categories'), staleTime: 60_000 });
  const collections = useQuery({ queryKey: ['collections', locale], queryFn: () => api<Taxonomy[]>('/api/store/collections'), staleTime: 60_000 });
  return { categories: categories.data ?? [], collections: collections.data ?? [] };
}

export function Logo({ className = '', withTagline = false }: { className?: string; withTagline?: boolean }) {
  const { store } = useSettings();
  const t = useT();
  if (store.logoUrl) return <img src={assetUrl(store.logoUrl)} alt={store.name} className={`h-8 w-auto md:h-10 ${className}`} />;
  return (
    <span className={`flex flex-col items-center leading-none ${className}`}>
      <span className="font-display text-[1.4rem] font-semibold tracking-[-0.01em] whitespace-nowrap sm:text-[1.7rem] md:text-[2.1rem]">{store.name}</span>
      {withTagline && <span className="typewriter mt-1.5 hidden text-[10.5px] tracking-[0.28em] text-stone uppercase sm:block">{t.est}</span>}
    </span>
  );
}

/** EN · LT — keeps you on the same page in the other language. */
export function LanguageSwitch({ className = '' }: { className?: string }) {
  const locale = useLocale();
  const t = useT();
  const href = useAlternateHref();
  return (
    <nav aria-label={t.header.language} className={`typewriter flex items-center gap-1 text-[12.5px] font-bold tracking-[0.12em] ${className}`}>
      {LOCALES.map((l, i) => (
        <span key={l} className="flex items-center gap-1">
          {i > 0 && <span className="text-ash" aria-hidden>·</span>}
          <RouterLink
            to={href(l)}
            hrefLang={l}
            lang={l}
            aria-current={l === locale ? 'true' : undefined}
            aria-label={LOCALE_NAMES[l]}
            className={`rounded-[2px] px-1.5 py-1 uppercase transition ${l === locale ? 'bg-ink text-paper' : 'text-stone hover:text-ink'}`}
          >
            {l}
          </RouterLink>
        </span>
      ))}
    </nav>
  );
}

export function AnnouncementBar() {
  const { store } = useSettings();
  if (!store.announcement.enabled || !store.announcement.text) return null;
  const content = <span>{store.announcement.text}</span>;
  return (
    <div className="bg-ink text-paper">
      <div className="container-page typewriter flex min-h-9 items-center justify-center py-2 text-center text-[10.5px] tracking-[0.06em] text-balance uppercase sm:text-[11.5px] sm:tracking-[0.14em]">
        {store.announcement.link ? (
          <Link to={store.announcement.link} className="link-underline">
            {content}
          </Link>
        ) : (
          content
        )}
      </div>
    </div>
  );
}

export function Header() {
  const t = useT();
  const { categories, collections } = useTaxonomy();
  const [scrolled, setScrolled] = useState(false);
  const [mega, setMega] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    setMega(false);
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  const navLink = 'typewriter link-underline py-1 text-[12.5px] font-bold tracking-[0.12em] uppercase';

  return (
    <header
      className={`sticky top-0 z-40 transition-[background-color,box-shadow] duration-500 ${scrolled || mega ? 'bg-bone/95 shadow-[0_8px_20px_-16px_rgb(60_40_20_/_0.5)] backdrop-blur-md' : 'bg-bone'}`}
      onMouseLeave={() => setMega(false)}
    >
      <div className="container-page grid h-16 grid-cols-[1fr_auto_1fr] items-center gap-3 md:h-[88px]">
        <nav className="flex items-center gap-7" aria-label={t.header.main}>
          <button className="-ml-2 p-2 lg:hidden" onClick={() => setMobileOpen(true)} aria-label={t.header.openMenu}>
            <Menu className="h-5 w-5" strokeWidth={1.6} />
          </button>
          <div className="hidden items-center gap-6 lg:flex">
            <button className={navLink} onMouseEnter={() => setMega(true)} onFocus={() => setMega(true)} onClick={() => setMega((m) => !m)} aria-expanded={mega} aria-haspopup="true">
              {t.header.shop}
            </button>
            {categories.slice(0, 2).map((c) => (
              <NavLink key={c.id} to={`/categories/${c.slug}`} className={navLink} onMouseEnter={() => setMega(false)}>
                {c.name}
              </NavLink>
            ))}
          </div>
        </nav>
        <Link to="/" className="justify-self-center" aria-label={t.common.home}>
          <Logo withTagline />
        </Link>
        <div className="flex items-center justify-end gap-1 md:gap-4">
          <LanguageSwitch className="hidden sm:flex" />
          <button className="-mr-2 flex items-center gap-2 p-2" onClick={() => setSearchOpen(true)} aria-label={t.header.search}>
            <Search className="h-[19px] w-[19px]" strokeWidth={1.6} />
            <span className={`${navLink} hidden xl:inline`}>{t.header.search}</span>
          </button>
        </div>
      </div>
      <div className="container-page">
        <div className="rule-double" />
      </div>

      {/* Mega menu */}
      <div
        className={`absolute inset-x-0 top-full hidden bg-paper shadow-[0_24px_40px_-30px_rgb(60_40_20_/_0.6)] transition-all duration-500 lg:block ${mega ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-2 opacity-0'}`}
        onMouseEnter={() => setMega(true)}
      >
        <div className="container-page grid grid-cols-12 gap-10 py-10">
          <div className="col-span-3">
            <p className="eyebrow mb-5">{t.header.categories}</p>
            <ul className="space-y-2.5">
              <li>
                <Link to="/" className="font-display link-underline text-[1.6rem] font-medium">
                  {t.common.shopAll}
                </Link>
              </li>
              {categories.map((c) => (
                <li key={c.id}>
                  <Link to={`/categories/${c.slug}`} className="font-display link-underline text-[1.6rem] font-medium">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-3">
            <p className="eyebrow mb-5">{t.header.collections}</p>
            <ul className="space-y-3 text-[17px]">
              {collections.map((c) => (
                <li key={c.id}>
                  <Link to={`/collections/${c.slug}`} className="link-underline">
                    {c.name}
                  </Link>
                </li>
              ))}
              <li className="pt-1">
                <Link to="/?onSale=true" className="link-underline text-sale">
                  {t.common.reduced}
                </Link>
              </li>
            </ul>
          </div>
          {categories.slice(0, 2).map((c, i) => (
            <Link key={c.id} to={`/categories/${c.slug}`} className="group col-span-3 block">
              <div className={`photo ${i % 2 ? 'rotate-[0.8deg]' : '-rotate-[0.8deg]'} transition-transform group-hover:rotate-0`}>
                <div className="photo-inner aspect-[4/3]">
                  <Img src={c.imageUrl} alt={c.name} sizes="25vw" className="h-full w-full object-cover group-hover:scale-[1.04]" />
                </div>
              </div>
              <p className="font-display mt-4 flex items-center gap-2 text-lg font-medium">
                {c.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </p>
            </Link>
          ))}
        </div>
        <div className="h-[3px] border-t border-b border-ink/20" />
      </div>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <MobileMenu open={mobileOpen} onOpenChange={setMobileOpen} categories={categories} collections={collections} />
    </header>
  );
}

function MobileMenu({ open, onOpenChange, categories, collections }: { open: boolean; onOpenChange: (o: boolean) => void; categories: Taxonomy[]; collections: Taxonomy[] }) {
  const { store } = useSettings();
  const t = useT();
  const { pages } = usePages();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/40 data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
        <Dialog.Content className="fixed inset-y-0 left-0 z-50 flex w-[88vw] max-w-sm flex-col bg-bone data-[state=closed]:animate-slide-out-left data-[state=open]:animate-slide-in-left">
          <div className="flex h-16 items-center justify-between border-b-[3px] border-double border-ink/60 px-5">
            <Dialog.Title className="font-display text-[1.45rem] font-semibold">{store.name}</Dialog.Title>
            <Dialog.Close className="-mr-2 p-2" aria-label={t.header.closeMenu}>
              <X className="h-5 w-5" strokeWidth={1.6} />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">{t.header.siteNav}</Dialog.Description>
          <nav className="flex-1 overflow-y-auto px-5 py-7">
            <LanguageSwitch className="mb-8" />
            <ul className="space-y-3.5">
              <li>
                <Link to="/" className="font-display text-[1.9rem] font-medium">
                  {t.common.shopAll}
                </Link>
              </li>
              {categories.map((c) => (
                <li key={c.id}>
                  <Link to={`/categories/${c.slug}`} className="font-display text-[1.9rem] font-medium">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="eyebrow mt-10 mb-4">{t.header.collections}</p>
            <ul className="space-y-3 text-[17px]">
              {collections.map((c) => (
                <li key={c.id}>
                  <Link to={`/collections/${c.slug}`}>{c.name}</Link>
                </li>
              ))}
              <li>
                <Link to="/?onSale=true" className="text-sale">
                  {t.common.reduced}
                </Link>
              </li>
            </ul>
            <p className="eyebrow mt-10 mb-4">{t.header.help}</p>
            <ul className="space-y-3 text-[17px]">
              {pages.map((p) => (
                <li key={p.slug}>
                  <Link to={`/pages/${p.slug}`}>{p.title}</Link>
                </li>
              ))}
            </ul>
          </nav>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
